/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'

import uniqWith from 'lodash/uniqWith'
import { useQuery } from 'react-query'
import styled from 'styled-components'

import FlexBox from 'src/components/Common/FlexBox'
import Spinner from 'src/components/Common/Spinner'
import {
  DonateCryptoDialog,
  DonateQRDialog,
  DonateWireDialog,
  LoadingDialog
} from 'src/components/Modal'
import { ADDRESS_ONE } from 'src/constants'
import { useModalContext } from 'src/contexts/ModalContext'
import { useThemeContext } from 'src/contexts/ThemeContext'
import { useWeb3Context } from 'src/contexts/Web3Context'
import { useModalV2Context } from 'src/features/modal-v2'
import { pxToVw } from 'src/functions/units'
import {
  batchFetchHistory,
  getDonationHistory,
  getDonationResultById,
  getLocalStorageNonRedemptionPaymentIds
} from 'src/services/donation'
import { DonationResultQueryData } from 'src/types/api'
import { CouldBe } from 'src/types/main'

import HistoryDetail from './ui/HistoryDetail'
import HistorySummary from './ui/HistorySummary'

const DonationHistory = () => {
  const { account } = useWeb3Context()
  const { colors } = useThemeContext()
  const { openModal, setModalConfig, setModalView } = useModalContext()
  const { closeModal } = useModalV2Context()

  const [donationHistory, setDonationHistory] = useState<
    Record<'accountHistory' | 'nonRedemptionHistory', CouldBe<DonationResultQueryData[]>>
  >({
    accountHistory: undefined,
    nonRedemptionHistory: undefined
  })
  const [historyCursor] = useState<number>(5)

  const nonRedemptionPaymentIds = useMemo(() => getLocalStorageNonRedemptionPaymentIds(), [])

  const sortedDonationHistory = useMemo(() => {
    if (!donationHistory.accountHistory || !donationHistory.nonRedemptionHistory) return

    const reducedHistory = [
      ...donationHistory.accountHistory,
      ...donationHistory.nonRedemptionHistory
    ]

    // descending sort all history based on the created datetime
    return uniqWith(reducedHistory, (a, b) => a.id === b.id).sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
  }, [donationHistory.accountHistory, donationHistory.nonRedemptionHistory])

  useEffect(() => {
    if (!nonRedemptionPaymentIds.length) {
      setDonationHistory((prev) => ({
        ...prev,
        nonRedemptionHistory: []
      }))
    }
  }, [nonRedemptionPaymentIds.length])

  useQuery(
    ['donationHistory', account],
    async () => (account ? await getDonationHistory(account) : []),
    {
      onSuccess: (data) => {
        setDonationHistory((prev) => ({
          ...prev,
          accountHistory: data
        }))
      }
    }
  )

  useQuery(['nonRedemptionDonationHistory'], () => batchFetchHistory(nonRedemptionPaymentIds), {
    enabled: !!nonRedemptionPaymentIds.length,
    onSuccess: (data) => {
      const filteredData = data.filter((historyItem) =>
        nonRedemptionPaymentIds.includes(historyItem.id)
      )

      setDonationHistory((prev) => ({
        ...prev,
        nonRedemptionHistory: filteredData
      }))
    }
  })

  const handleProceedToPayment = useCallback(
    async (paymentId: string, txId: string) => {
      closeModal('donation-history')

      openModal(<LoadingDialog />, txId, {
        fullscreen: false,
        showCloseButton: false,
        showDialogFooter: false,
        showDialogHeader: false
      })

      try {
        const result = await getDonationResultById(paymentId)

        const paymentType = result.description.split(' | ')[0].trim()
        if (
          paymentType !== 'QR Code' &&
          paymentType !== 'Bank Transfer' &&
          paymentType !== 'Cryptocurrency'
        ) {
          throw new Error('Unmatch payment type')
        }

        if (paymentType === 'QR Code') {
          setModalView(
            <DonateQRDialog
              expiredAt={result.expired_at}
              qrCodeImage={result.payment_method.qr_code_display_image}
              txId={result.tx_id}
              id={paymentId}
            />
          )
          setModalConfig((prev) => ({
            ...prev,
            showCloseButton: true,
            showDialogFooter: true,
            showDialogHeader: true,
            dialogTitle: 'Donate by Paynow QR'
          }))
        } else if (paymentType === 'Bank Transfer') {
          setModalView(
            <DonateWireDialog
              expiredAt={result.expired_at}
              paymentAmount={result.donation_amount}
              transferInstructions={result.payment_method.instructions}
              txId={result.tx_id}
            />
          )
          setModalConfig((prev) => ({
            ...prev,
            showCloseButton: true,
            showDialogFooter: true,
            showDialogHeader: true,
            dialogTitle: 'Donate by Bank Transfer'
          }))
        } else if (paymentType === 'Cryptocurrency') {
          setModalView(<DonateCryptoDialog hostedUrl={result.payment_method.hosted_url} />)
          setModalConfig((prev) => ({
            ...prev,
            showCloseButton: false,
            showDialogHeader: false,
            showDialogFooter: false
          }))
        }
      } catch (e: any) {
        console.error(e)
      }
    },
    [closeModal, openModal, setModalConfig, setModalView]
  )

  return (
    <FlexBox flexDirection="column" rowGap={26}>
      {sortedDonationHistory ? (
        sortedDonationHistory.length ? (
          sortedDonationHistory.slice(0, historyCursor).map((historyData) => (
            <StyledSummaryItem flexDirection="column" key={`historyItem_${historyData.id}`}>
              <HistorySummary
                description={historyData.description}
                donationAmount={historyData.donation_amount}
                expiredAt={new Date(historyData.expired_at).getTime()}
                status={historyData.status}
                blockReason={historyData.blocked_reasons}
                transactionHash={historyData.txn_hash}
                withRedemption={historyData.user_wallet_address !== ADDRESS_ONE}
                onProceedToPaymentClick={() =>
                  handleProceedToPayment(historyData.id, historyData.tx_id)
                }
              />
              <HistoryDetail status={historyData.status} />
            </StyledSummaryItem>
          ))
        ) : (
          <StyledEmptyMessage justifyContent="center" alignItems="center">
            Your donation history is still empty
          </StyledEmptyMessage>
        )
      ) : (
        <StyledSpinnerWrap justifyContent="center">
          <Spinner size={40} stroke={colors.primary} />
        </StyledSpinnerWrap>
      )}
    </FlexBox>
  )
}

export default DonationHistory

const StyledSummaryItem = styled(FlexBox)`
  width: 100%;

  &:last-child {
    margin-bottom: ${pxToVw(-31)};

    ${({ theme }) => theme.mediaQuery.mobile`
      margin-bottom: ${pxToVw(-31, 720)};
    `}
  }
`

const StyledSpinnerWrap = styled(FlexBox)`
  width: 100%;
`

const StyledEmptyMessage = styled(FlexBox)`
  font-size: ${pxToVw(24)};
  color: ${({ theme }) => theme.colors.gray600};
  text-align: center;
  width: 100%;
  height: ${pxToVw(40)};

  ${({ theme }) => theme.mediaQuery.mobile`
    margin-bottom: ${pxToVw(24, 720)};
    height: ${pxToVw(40, 720)};
  `}
`
